import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  cedenteNewOperationVisible,
  isCedenteNewOperationEnabled,
} from "../lib/cedenteOperations";

/**
 * GATE DE ENTRADA da nova operação do cedente (decisão 04(a) do owner:
 * toda superfície nova NASCE DESLIGADA).
 *
 * Duas travas, nesta ordem:
 *  1. flag de build `REACT_APP_CEDENTE_NEW_OPERATION` — ausente/qualquer coisa
 *     que não seja um "ligado" explícito ⇒ NADA renderiza e NENHUMA chamada é
 *     feita (nem o probe do catálogo);
 *  2. catálogo (`GET /cedente/products`) com ao menos um produto habilitado —
 *     sem regente resolvível o servidor devolve tudo cinza (fail-closed), e
 *     então a entrada continua escondida: não se mostra um botão que só erra.
 *
 * A ROTA em si é gateada só pela flag (App.js) — não dá para esperar um fetch
 * para montar a árvore de rotas; com a flag ligada e catálogo cinza o wizard
 * abre e trava no passo Produto (todos os cards cinza), que é o comportamento
 * fail-closed já existente.
 *
 * Toda a decisão testável vive em `lib/cedenteOperations.js`; aqui só a fiação.
 */
// Sidebar + página + tabela de clientes podem montar juntas: uma request só.
// A promise é cacheada POR IDENTIDADE DE USUÁRIO — trocar de conta na mesma aba
// não pode herdar o catálogo (nem a visibilidade) do usuário anterior.
let catalogCache = { key: null, promise: null };

function fetchCatalogOnce(userKey) {
  if (catalogCache.key !== userKey || !catalogCache.promise) {
    const promise = api
      .get("/cedente/products")
      .then(({ data }) => data)
      .catch(() => {
        // Falha ao provar o catálogo = não provado ⇒ segue escondido. E não
        // fica em cache: a próxima montagem tenta de novo.
        if (catalogCache.key === userKey) catalogCache = { key: null, promise: null };
        return { products: [], bo_unavailable: true, unavailable_reason: "internal" };
      });
    catalogCache = { key: userKey, promise };
  }
  return catalogCache.promise;
}

/**
 * @returns {{visible: boolean, unavailableReason: string|null}} —
 *   `unavailableReason` (`no_regent`|`bo_down`|`internal`) é o campo ADITIVO do
 *   backend, repassado para quem quiser diferenciar "o seu cadastro" de
 *   "tente de novo em instantes".
 */
export default function useCedenteNewOperationGate() {
  const { user } = useAuth();
  const flagOn = isCedenteNewOperationEnabled();
  // Identidade no cache: `cedente_id` entra junto porque o catálogo é resolvido
  // pelo REGENTE do cedente do token, não só pelo usuário.
  const userKey = user ? `${user.id || user.email || "?"}|${user.cedente_id || "?"}` : null;
  // null = ainda não se sabe ⇒ invisível (nada pisca na tela).
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    if (!flagOn || !userKey) {
      setCatalog(null); // troca/saída de usuário: esquece o catálogo anterior
      return undefined;
    }
    let alive = true;
    fetchCatalogOnce(userKey).then((data) => {
      if (alive) setCatalog(data);
    });
    return () => {
      alive = false;
    };
  }, [flagOn, userKey]);

  return {
    visible: cedenteNewOperationVisible({ flagOn, catalog }),
    unavailableReason: catalog?.unavailable_reason || null,
  };
}
